/**
 * curlNoise — divergence-free 3D turbulence for particle advection.
 *
 * A curl-noise field is the curl of a vector potential **P**:
 *
 *     v = ∇ × P
 *
 * The curl of any smooth field is analytically divergence-free
 * (∇·(∇×P) = 0), so advecting particles by `v` neither compresses nor
 * rarefies them — they swirl without clumping or tearing. We approximate the
 * three partial derivatives by central finite differences of a value-noise
 * potential; because the discrete curl reuses the same step `h`, its discrete
 * divergence also cancels to ~O(h²) truncation error (the property the unit
 * tests check numerically).
 *
 * This module is the CPU REFERENCE. `curlNoiseTSL` in `kernels.ts` mirrors the
 * exact same math in TSL so the GPU update kernel and these tests agree. Keep
 * the two in lock-step — any change to the hash, fade, or lattice math here
 * must be mirrored there (and vice-versa).
 */

// ─── Deterministic value-noise lattice ───────────────────────────────────────

/**
 * Integer hash → float in [0,1). A cheap, well-mixed 32-bit integer bit-mix
 * (Wang/Jenkins style). Deterministic and platform-stable: uses only Math
 * ops that round-trip identically in JS and WGSL (`>>> 0` mirrors WGSL u32
 * wraparound). The TSL side reproduces this with `.bitXor`/`.shiftRight`.
 */
export function hash3(ix: number, iy: number, iz: number, seed: number): number {
  // Combine lattice coords with large odd primes, then avalanche.
  let h = (ix | 0) * 374761393 + (iy | 0) * 668265263 + (iz | 0) * 2147483647 + (seed | 0) * 1274126177;
  h = h >>> 0;
  h ^= h >>> 13;
  h = (h * 1274126177) >>> 0;
  h ^= h >>> 16;
  // Map the top 24 bits to [0,1) for a stable mantissa.
  return (h >>> 8) / 16777216;
}

/** Quintic smootherstep fade (Perlin's 6t⁵−15t⁴+10t³) — C² continuous. */
export function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Trilinearly-interpolated value noise in [0,1] at (x,y,z) for a given seed.
 * One scalar octave — the potential field stacks three of these with offset
 * seeds to form a vector potential.
 */
export function valueNoise3(x: number, y: number, z: number, seed: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const ux = fade(fx), uy = fade(fy), uz = fade(fz);

  const c000 = hash3(x0,     y0,     z0,     seed);
  const c100 = hash3(x0 + 1, y0,     z0,     seed);
  const c010 = hash3(x0,     y0 + 1, z0,     seed);
  const c110 = hash3(x0 + 1, y0 + 1, z0,     seed);
  const c001 = hash3(x0,     y0,     z0 + 1, seed);
  const c101 = hash3(x0 + 1, y0,     z0 + 1, seed);
  const c011 = hash3(x0,     y0 + 1, z0 + 1, seed);
  const c111 = hash3(x0 + 1, y0 + 1, z0 + 1, seed);

  const x00 = lerp(c000, c100, ux);
  const x10 = lerp(c010, c110, ux);
  const x01 = lerp(c001, c101, ux);
  const x11 = lerp(c011, c111, ux);
  const y0i = lerp(x00, x10, uy);
  const y1i = lerp(x01, x11, uy);
  return lerp(y0i, y1i, uz);
}

// Three decorrelated seeds for the three potential components. Mirrored in TSL.
export const POTENTIAL_SEED_X = 0;
export const POTENTIAL_SEED_Y = 31337;
export const POTENTIAL_SEED_Z = 65539;

/** Vector potential P(p) = (noise_x, noise_y, noise_z) at world point p. */
export function potential(
  x: number, y: number, z: number,
): { px: number; py: number; pz: number } {
  return {
    px: valueNoise3(x, y, z, POTENTIAL_SEED_X),
    py: valueNoise3(x, y, z, POTENTIAL_SEED_Y),
    pz: valueNoise3(x, y, z, POTENTIAL_SEED_Z),
  };
}

/** Finite-difference step for the curl. Small relative to lattice spacing. */
export const CURL_EPSILON = 1.0e-3;

/**
 * Curl-noise velocity at world point p:
 *
 *     v = ( ∂Pz/∂y − ∂Py/∂z,
 *           ∂Px/∂z − ∂Pz/∂x,
 *           ∂Py/∂x − ∂Px/∂y )
 *
 * Each partial is a central difference with step `h = CURL_EPSILON`. The
 * returned vector is divergence-free up to O(h²).
 */
export function curlNoise(
  x: number, y: number, z: number,
  h: number = CURL_EPSILON,
): { x: number; y: number; z: number } {
  const inv2h = 1 / (2 * h);

  // ∂Pz/∂y and ∂Py/∂z  → vx
  const pZ_yp = valueNoise3(x, y + h, z, POTENTIAL_SEED_Z);
  const pZ_yn = valueNoise3(x, y - h, z, POTENTIAL_SEED_Z);
  const pY_zp = valueNoise3(x, y, z + h, POTENTIAL_SEED_Y);
  const pY_zn = valueNoise3(x, y, z - h, POTENTIAL_SEED_Y);
  const vx = (pZ_yp - pZ_yn) * inv2h - (pY_zp - pY_zn) * inv2h;

  // ∂Px/∂z and ∂Pz/∂x  → vy
  const pX_zp = valueNoise3(x, y, z + h, POTENTIAL_SEED_X);
  const pX_zn = valueNoise3(x, y, z - h, POTENTIAL_SEED_X);
  const pZ_xp = valueNoise3(x + h, y, z, POTENTIAL_SEED_Z);
  const pZ_xn = valueNoise3(x - h, y, z, POTENTIAL_SEED_Z);
  const vy = (pX_zp - pX_zn) * inv2h - (pZ_xp - pZ_xn) * inv2h;

  // ∂Py/∂x and ∂Px/∂y  → vz
  const pY_xp = valueNoise3(x + h, y, z, POTENTIAL_SEED_Y);
  const pY_xn = valueNoise3(x - h, y, z, POTENTIAL_SEED_Y);
  const pX_yp = valueNoise3(x, y + h, z, POTENTIAL_SEED_X);
  const pX_yn = valueNoise3(x, y - h, z, POTENTIAL_SEED_X);
  const vz = (pY_xp - pY_xn) * inv2h - (pX_yp - pX_yn) * inv2h;

  return { x: vx, y: vy, z: vz };
}

/**
 * Numerically estimate the divergence ∇·v of the curl-noise field at a point,
 * using central differences with step `d`. Used by the unit tests to assert
 * the divergence-free property (result ≈ 0).
 */
export function divergenceOfCurl(
  x: number, y: number, z: number,
  d: number = 1.0e-2,
): number {
  const inv2d = 1 / (2 * d);
  const dvx = (curlNoise(x + d, y, z).x - curlNoise(x - d, y, z).x) * inv2d;
  const dvy = (curlNoise(x, y + d, z).y - curlNoise(x, y - d, z).y) * inv2d;
  const dvz = (curlNoise(x, y, z + d).z - curlNoise(x, y, z - d).z) * inv2d;
  return dvx + dvy + dvz;
}
